import { createDateColumn, createMappedColumn, createRenderColumn, createSimpleColumn, createSimpleColumnWithFallback } from "../common/TableComponent";
import { getScoringUserSegmentName, ScoringTableType, ScoringTabType } from "../../types/api/scoring/scoring";
import { UsersAggregatedLeadScoringResponse } from '../../types/api/scoring/tables/UsersAggregatedLeadScoringResponse';
import { CatalogsAggregatedLeadScoringResponse } from '../../types/api/scoring/tables/CatalogsAggregatedLeadScoringResponse';
import { CatalogsDetailedLeadScoringResponse } from '../../types/api/scoring/tables/CatalogsDetailedLeadScoringResponse';
import { CategoriesAggregatedLeadScoringResponse } from '../../types/api/scoring/tables/CategoriesAggregatedLeadScoringResponse';
import { SegmentsAggregatedLeadScoringResponse } from '../../types/api/scoring/tables/SegmentsAggregatedLeadScoringResponse';
import { SegmentsDetailedLeadScoringResponse } from '../../types/api/scoring/tables/SegmentsDetailedLeadScoringResponse';
import { UsersDetailedLeadScoringResponse } from "../../types/api/scoring/tables/UsersDetailedLeadScoringResponse";

export const emptyListDescription = `По вашему запросу не найдено
подходящих данных`;

// Users
export const usersAggregatedLeadScoringColumns = [
    createSimpleColumnWithFallback<UsersAggregatedLeadScoringResponse>(['userNumber', 'userId'], 'Пользователь ID'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('phoneNumber', 'Номер телефона'),
    createRenderColumn<UsersAggregatedLeadScoringResponse>(
        'role',
        'Роль',
        (row) => `${row.globalRole?.toUpperCase()}`
    ),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('regCountryTitle', 'Страна регистрации', false, true),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('regCityTitle', 'Город регистрации'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('currentCountry', 'Текущая страна', false, true),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('currentCity', 'Текущий город'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('username', 'Имя аккаунта'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('contacts', 'Контакты (Номер телефона, WA,  Viber, Telegram)'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('categoriesVisited', 'Разделов посещено'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('catalogsVisited', 'Подразделов посещено'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('totalAdViews', 'Просмотры объявлений (всего)', false, true),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('totalFavorites', 'Добавлено в избранное (всего)', false, true),
    createRenderColumn<UsersAggregatedLeadScoringResponse>(
        'time',
        'Время в разделах (мин.) общее/среднее',
        (row) => `${row.totalTimeMinutesInCategories}/${row.avgTimeMinutesInCategories}`,
        false,
        true
    ),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('totalContactAttempts', 'Клики/нажатия на “Написать”, “Показать номер” (всего)'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('totalMessages', 'Сообщений (всего)'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('totalProfileViews', 'Переходы в профиль продавца (всего)'),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('totalActiveAds', 'Активные объявления', false, true),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('totalArchivedAds', 'Архивные объявления', false, true),
    createSimpleColumn<UsersAggregatedLeadScoringResponse>('globalScore', 'Итоговый скоринг', false, true),
];

export const usersDetailedLeadScoringColumns = [
    createSimpleColumnWithFallback<UsersDetailedLeadScoringResponse>(['userNumber', 'userId'], 'Пользователь ID'),
    createRenderColumn<UsersDetailedLeadScoringResponse>(
        'localRole',
        'Роль',
        (row) => `${row.localRole?.toUpperCase()}`
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'phoneNumber',
        'Номер телефона регистрации'
    ),
    createDateColumn<UsersDetailedLeadScoringResponse>(
        'date',
        'Дата',
    ),
    createRenderColumn<UsersDetailedLeadScoringResponse>(
        'search',
        'Геолокация поиска',
        (row) => {
            const country = row.searchCountryTitle || '';
            const city = row.searchCityTitle || '';

            if (!country && !city) return '';
            if (!country) return city;
            if (!city) return country;

            return `${country}/${city}`;
        }
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'categoryTitle',
        'Раздел'
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'catalogTitle',
        'Подраздел'
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'searchTerm',
        'Фраза поиска'
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'keywordTitle',
        'Ключевые слова'
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'totalAdViews',
        'Просмотры',
        false,
        true
    ),
    {
        id: 'favoritesIds' as keyof UsersDetailedLeadScoringResponse,
        title: 'Избранное (кол), ID)',
        sortable: true,
        filtered: true,
        render: (row: UsersDetailedLeadScoringResponse) => {
            const count = row.totalFavoritesAdded;
            const ids = row.favoritesIds || row.favoritesNumbers;
            return ids ? `${count} (${ids})` : count.toString();
        }
    },
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'totalDwellTimeMinutes',
        'Время (мин.) раздел/подраздел',
        false,
        true
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'totalContactAttempts',
        'Клики/нажатия на "Написать", "Показать номер" (всего)',
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'totalMessagesSent',
        'Сообщения',
        false,
        true
    ),
    createSimpleColumn<UsersDetailedLeadScoringResponse>(
        'totalProfileViews',
        'Переходы в профиль',
    )
];

// Subsections
export const catalogsAggregatedLeadScoringColumns = [
    createSimpleColumn<CatalogsAggregatedLeadScoringResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<CatalogsAggregatedLeadScoringResponse>('catalogTitle', 'Подраздел'),
    createRenderColumn<CatalogsAggregatedLeadScoringResponse>(
        'totalUsers',
        'Количество пользователей',
        (row) => `${row.totalLeads + row.totalSellers}`,
        false,
        true
    ),
    createRenderColumn<CatalogsAggregatedLeadScoringResponse>(
        'leadScoring',
        'LEAD: количество / скоринг',
        (row) => `${row.totalLeads}/${row.totalLeadsScore}`,
        false,
        true
    ),
    createRenderColumn<CatalogsAggregatedLeadScoringResponse>(
        'sellerScoring',
        'SELLER: количество / скоринг',
        (row) => `${row.totalSellers}/${row.totalSellersScore}`,
        false,
        true
    ),
    createSimpleColumn<CatalogsAggregatedLeadScoringResponse>('totalAdViews', 'Просмотры (всего)', false, true),
    createSimpleColumn<CatalogsAggregatedLeadScoringResponse>('totalFavoritesAdded', 'Добавлено в избранное (всего)', false, true),
    createSimpleColumn<CatalogsAggregatedLeadScoringResponse>('totalContactAttempts', 'Клики/нажатия на “Написать”,  “Показать номер” (всего)'),
    createSimpleColumn<CatalogsAggregatedLeadScoringResponse>('totalMessagesSent', 'Сообщения (всего)'),
    createRenderColumn<CatalogsAggregatedLeadScoringResponse>(
        'timeStats',
        'Время (мин.) общее/среднее',
        (row) => `${row.totalTimeMinutes}/${row.avgTimeMinutes}`,
        false,
        true
    )
];

export const catalogsDetailedLeadScoringColumns = [
    createSimpleColumnWithFallback<CatalogsDetailedLeadScoringResponse>(['userNumber', 'userId'], 'Пользователь ID'),
    createRenderColumn<CatalogsDetailedLeadScoringResponse>(
        'localRole',
        'Роль',
        (row) => `${row.localRole?.toUpperCase()}`
    ),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('catalogTitle', 'Подраздел'),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('adViewsCount', 'Просмотры', false, true),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('favoritesIds', 'Избранное (ID)', false, true),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('contactAttemptsCount', 'Клики/нажатия на “Написать”,  “Показать номер” (всего)'),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('messagesSentCount', 'Сообщения', false, true),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('dwellTimeMinutes', 'Время (мин.)', false, true),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('activeAdsCount', 'Активные объявления', false, true),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('totalArchivedAds', 'Архивные объявления', false, true),
    createSimpleColumn<CatalogsDetailedLeadScoringResponse>('localScore', 'Скоринг', false, true)
];

// Sections
export const categoriesAggregatedLeadScoringColumns = [
    createSimpleColumn<CategoriesAggregatedLeadScoringResponse>('categoryTitle', 'Раздел'),
    createSimpleColumn<CategoriesAggregatedLeadScoringResponse>('totalUsers', 'Количество пользователей', false, true),
    createRenderColumn<CategoriesAggregatedLeadScoringResponse>(
        'leadCount',
        'LEAD: количество / скоринг',
        (row) => `${row.totalLeads}/${row.totalLeadsScore}`,
        false,
        true
    ),
    createRenderColumn<CategoriesAggregatedLeadScoringResponse>(
        'sellerCount',
        'SELLER: количество / скоринг',
        (row) => `${row.totalSellers}/${row.totalSellersScore}`,
        false,
        true
    ),
    createSimpleColumn<CategoriesAggregatedLeadScoringResponse>('totalAdViews', 'Просмотры (всего)', false, true),
    createSimpleColumn<CategoriesAggregatedLeadScoringResponse>('totalFavoritesAdded', 'Избранное (всего)', false, true),
    createSimpleColumn<CategoriesAggregatedLeadScoringResponse>('totalContactAttempts', 'Клики/нажатия на “Написать”,  “Показать номер” (всего)'),
    createSimpleColumn<CategoriesAggregatedLeadScoringResponse>('totalMessagesSent', 'Сообщения (всего)'),
    createSimpleColumn<CategoriesAggregatedLeadScoringResponse>('totalProfileViews', 'Переходы в профиль'),
    createRenderColumn<CategoriesAggregatedLeadScoringResponse>(
        'time',
        'Время (мин.) общее/среднее',
        (row) => `${row.totalDwellTimeMinutes}/${row.avgDwellTimeMinutes}`,
        false,
        true
    )
];

// Segments
export const segmentsAggregatedLeadScoringColumns = [
    createMappedColumn<SegmentsAggregatedLeadScoringResponse>('segment', 'Сегмент', getScoringUserSegmentName),
    createSimpleColumn<SegmentsAggregatedLeadScoringResponse>('scoreRange', 'Диапазон счета'),
    createSimpleColumn<SegmentsAggregatedLeadScoringResponse>('totalUsers', 'Всего пользователей'),
    createSimpleColumn<SegmentsAggregatedLeadScoringResponse>('avgScore', 'Средний счет'),
    createSimpleColumn<SegmentsAggregatedLeadScoringResponse>('avgAdViews', 'Средние просмотры объявлений'),
    createSimpleColumn<SegmentsAggregatedLeadScoringResponse>('avgContactAttempts', 'Средние попытки связи')
];

export const segmentsDetailedLeadScoringColumns = [
    createSimpleColumnWithFallback<SegmentsDetailedLeadScoringResponse>(['userNumber', 'userId'], 'Пользователь ID'),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('phoneNumber', 'Номер телефона'),
    createRenderColumn<SegmentsDetailedLeadScoringResponse>(
        'role',
        'Роль',
        (row) => `${row.globalRole?.toUpperCase()}`
    ),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('currentCountry', 'Текущая страна', false, true),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('currentCity', 'Текущий город'),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('totalAdViews', 'Просмотры объявлений (всего)', false, true),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('totalFavorites', 'Добавлено в избранное (всего)', false, true),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('totalTimeMinutesInCategories', 'Время (мин.) общее', false, true),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('totalContactAttempts', 'Клики/нажатия на “Написать”,  “Показать номер” (всего)'),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('totalMessages', 'Сообщений (всего)', false, true),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('totalActiveAds', 'Активные объявления', false, true),
    createSimpleColumn<SegmentsDetailedLeadScoringResponse>('globalScore', 'Итоговый скоринг', false, true),
    createMappedColumn<SegmentsDetailedLeadScoringResponse>('segment', 'Сегмент', getScoringUserSegmentName)
];

// Methods
export const columnsForCurrentTab = (tab: ScoringTabType, type: ScoringTableType) => {
    switch (tab) {
        case 'users':
            switch (type) {
                case 'aggregated':
                    return usersAggregatedLeadScoringColumns
                case 'detailed':
                    return usersDetailedLeadScoringColumns
            }
            break
        case 'catalogs':
            switch (type) {
                case 'aggregated':
                    return catalogsAggregatedLeadScoringColumns
                case 'detailed':
                    return catalogsDetailedLeadScoringColumns
            }
            break
        case 'categories':
            return categoriesAggregatedLeadScoringColumns
        case 'segments':
            switch (type) {
                case 'aggregated':
                    return segmentsAggregatedLeadScoringColumns
                case 'detailed':
                    return segmentsDetailedLeadScoringColumns
            }
            break
        default:
            return []
    }
}  